import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Mail,
  CreditCard,
  Pencil,
  UserX,
  UserCheck,
  CalendarRange,
  Check,
  X,
  Dumbbell,
  Plus,
  Copy,
  Ban,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import type { MemberDetail, Payment, CheckInStreak } from '@techsofit/api-types';
import { Button, Card, StatusBadge, Avatar, Skeleton, EmptyState, Textarea, Modal, Input, useConfirm } from '@techsofit/ui';
import { ActionMenu } from '../components/ActionMenu';
import { GoalsCard } from '../components/GoalsCard';
import { StreakWeekCard } from '../components/StreakWeekCard';
import { getMember, getMemberPayments, getMemberWorkouts, getMemberStreak, deactivateMember, activateMember, updateMember, voidPayment, deleteMember } from '../lib/gymApi';
import { VoidPaymentModal } from '../components/VoidPaymentModal';
import { ApiClientError, useAuth } from '@techsofit/app-kit';
import { useAsync } from '../lib/useAsync';
import { statusToBadge } from '../lib/membership';
import { money, formatDate, relativeDays, todayIso } from '../lib/format';

export function MemberDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';

  const { data: member, loading, error, reload } = useAsync<MemberDetail>(() => getMember(id), [id]);
  const { data: payments, reload: reloadPayments } = useAsync(() => getMemberPayments(id), [id]);
  const [voidingId, setVoidingId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<Payment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const hasRealPayments = (payments ?? []).some((p) => !p.voided);
  const { data: allWorkouts } = useAsync(() => getMemberWorkouts(id), [id]);
  const { data: streak } = useAsync<CheckInStreak>(() => getMemberStreak(id), [id]);
  // Coaches schedule ahead — show only today + upcoming, not past history.
  const asOf = todayIso();
  const workouts = (allWorkouts ?? []).filter((w) => w.scheduledDate >= asOf);

  // Inline notes editing.
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  function startEditNotes() {
    setNotesDraft(member?.notes ?? '');
    setEditingNotes(true);
  }

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await updateMember(id, { notes: notesDraft.trim() });
      setEditingNotes(false);
      reload();
    } finally {
      setSavingNotes(false);
    }
  }

  if (loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-4 h-40" />
        <Skeleton className="mt-3 h-64" />
      </section>
    );
  }
  if (error || !member) {
    return (
      <section className="animate-fade-up">
        <BackLink onClick={() => navigate('/members')} />
        <EmptyState title="Member not found" description={error ?? 'They may have been deactivated.'} />
      </section>
    );
  }


  async function onDeactivate() {
    if (!member) return;
    const ok = await confirm({
      title: `Deactivate ${member.fullName}?`,
      description: "They'll be hidden from the members list.",
      confirmLabel: 'Deactivate',
      tone: 'danger',
    });
    if (!ok) return;
    await deactivateMember(member.id);
    navigate('/members');
  }

  async function onReactivate() {
    if (!member) return;
    const ok = await confirm({
      title: `Reactivate ${member.fullName}?`,
      description: "They'll return to the members list.",
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;
    await activateMember(member.id);
    navigate('/members');
  }

  async function doVoidPayment(reason: string) {
    const p = voidTarget;
    if (!p) return;
    setVoidingId(p.id);
    try {
      await voidPayment(p.id, reason);
      await Promise.all([reloadPayments(), reload()]); // refresh history + recomputed status
      setVoidTarget(null);
    } finally {
      setVoidingId(null);
    }
  }

  return (
    <section className="animate-fade-up">
      <BackLink onClick={() => navigate('/members')} />

      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <Avatar name={member.fullName} size="lg" />
        <div className="min-w-0">
          <h1 className="font-display text-display-md text-ink">{member.fullName}</h1>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={statusToBadge(member.status)} />
            <span className="text-sm text-muted">{relativeDays(member.daysToExpiry)}</span>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => navigate('/payments/new', { state: { memberId: member.id } })}>
            <CreditCard className="size-4" aria-hidden />
            <span className="hidden sm:inline">Record payment</span>
          </Button>
          <Button variant="outline" onClick={() => navigate(`/members/${member.id}/edit`)}>
            <Pencil className="size-4" aria-hidden />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <ActionMenu
            items={[
              member.active
                ? { label: 'Deactivate member', icon: <UserX className="size-4" aria-hidden />, danger: true, onClick: onDeactivate }
                : { label: 'Reactivate member', icon: <UserCheck className="size-4" aria-hidden />, onClick: onReactivate },
              { label: 'Delete member', icon: <Trash2 className="size-4" aria-hidden />, danger: true, onClick: () => setDeleting(true) },
            ]}
          />
        </div>
      </div>

      {deleting && (
        <DeleteMemberModal
          member={member}
          hasRealPayments={hasRealPayments}
          onClose={() => setDeleting(false)}
          onDeactivate={() => { setDeleting(false); onDeactivate(); }}
          onDeleted={() => navigate('/members')}
        />
      )}

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {/* Contact */}
        <Card className="p-5">
          <h2 className="text-sm font-medium text-ink">Contact</h2>
          <dl className="mt-3 space-y-3 text-sm">
            <ContactRow icon={<Phone className="size-4" aria-hidden />} label="Call" value={member.phone} />
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 text-muted">
                <MessageCircle className="size-4" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-muted">WhatsApp</p>
                {member.whatsappPhone ? (
                  <p className="font-medium text-ink tabular">{member.whatsappPhone}</p>
                ) : (
                  <p className="font-medium text-ink-soft">
                    None — reminders go to {member.email ? 'email' : 'no channel'}
                  </p>
                )}
              </div>
            </div>
            <ContactRow icon={<Mail className="size-4" aria-hidden />} label="Email" value={member.email ?? '—'} />
          </dl>
        </Card>

        {/* Membership — ongoing gym access; a membership payment sets the expiry. */}
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-ink">Membership</h2>
            <StatusBadge status={statusToBadge(member.status)} />
          </div>
          <dl className="mt-3 space-y-3 text-sm">
            <InfoRow label="Plan" value={member.currentPlanName ?? 'No plan'} />
            <InfoRow label="Expiry" value={formatDate(member.currentExpiry)} />
            <InfoRow label="Joined" value={formatDate(member.joinDate)} />
          </dl>
          <p className="mt-3 text-xs text-muted">Renewing a membership extends this expiry date.</p>
        </Card>

        {/* Personal training — billed separately; does NOT change the membership expiry. */}
        <Card className="p-5">
          <h2 className="text-sm font-medium text-ink">Personal training</h2>
          <dl className="mt-3 space-y-3 text-sm">
            <InfoRow
              label="Status"
              value={
                member.ptStatus === 'NEVER_PAID'
                  ? 'None paid'
                  : member.ptStatus === 'LAPSED'
                    ? `Expired ${formatDate(member.ptExpiry)}`
                    : member.ptStatus === 'EXPIRING'
                      ? `Expiring ${formatDate(member.ptExpiry)}`
                      : `Active until ${formatDate(member.ptExpiry)}`
              }
            />
            <InfoRow label="Coach" value={member.coachName ?? 'None'} />
          </dl>
          <p className="mt-3 text-xs text-muted">Billed separately — PT payments don’t change the membership expiry.</p>
        </Card>

        {/* Notes — inline editable */}
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-ink">Notes</h2>
            {!editingNotes && (
              <button
                onClick={startEditNotes}
                className="inline-flex items-center gap-1 text-xs font-medium text-ink-soft hover:text-ink"
              >
                <Pencil className="size-3.5" aria-hidden />
                Edit
              </button>
            )}
          </div>

          {editingNotes ? (
            <div className="mt-3">
              <Textarea
                label=""
                aria-label="Member notes"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Add a note…"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditingNotes(false)} disabled={savingNotes}>
                  <X className="size-4" aria-hidden /> Cancel
                </Button>
                <Button size="sm" onClick={saveNotes} loading={savingNotes}>
                  {!savingNotes && <Check className="size-4" aria-hidden />} Save
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={startEditNotes}
              className="mt-3 block w-full whitespace-pre-wrap text-left text-sm text-ink-soft"
            >
              {member.notes?.trim() ? member.notes : <span className="text-muted">No notes yet — click to add.</span>}
            </button>
          )}
        </Card>
      </div>

      {streak && (
        <div className="mt-3">
          <StreakWeekCard streak={streak} name={member.fullName} />
        </div>
      )}

      {/* Goals */}
      <GoalsCard memberId={member.id} />

      {/* Workouts — build one on the fly for this member, or duplicate a previous one. */}
      <Card className="mt-3 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h2 className="font-display text-lg font-semibold text-ink">Workouts</h2>
          <Button
            size="sm"
            onClick={() =>
              navigate(`/workouts/new?member=${member.id}&name=${encodeURIComponent(member.fullName.split(' ')[0])}`)
            }
          >
            <Plus className="size-4" aria-hidden /> Add workout
          </Button>
        </div>
        {workouts.length ? (
          <ul className="divide-y divide-line">
            {workouts.map((w) => (
              <li key={w.id} className="flex items-center gap-3 px-5 py-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-control bg-surface text-ink-soft">
                  <Dumbbell className="size-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">{w.workoutName}</p>
                  <p className="text-xs text-muted tabular">
                    {formatDate(w.scheduledDate)}{w.timeLabel ? ` · ${w.timeLabel}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/workouts/new?member=${member.id}&name=${encodeURIComponent(member.fullName.split(' ')[0])}&from=${w.workoutId}`,
                    )
                  }
                  className="inline-flex shrink-0 items-center gap-1 rounded-pill px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink"
                >
                  <Copy className="size-4" aria-hidden /> Duplicate
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-muted">
            No workouts scheduled yet. Add one to start their program.
          </p>
        )}
      </Card>

      {/* Payment history */}
      <Card className="mt-3 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h2 className="font-display text-lg font-semibold text-ink">Payment history</h2>
          <CalendarRange className="size-4 text-muted" aria-hidden />
        </div>
        {payments && payments.length ? (
          <ul className="divide-y divide-line">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className={`flex items-center gap-1.5 font-medium text-ink ${p.voided ? 'line-through opacity-60' : ''}`}>
                    <span>{money(p.amount)} · {p.method}</span>
                    <span
                      className={`rounded-pill px-2 py-0.5 text-xs font-medium ${
                        p.type === 'PERSONAL_TRAINING' ? 'bg-accent-soft text-ink' : 'bg-surface text-ink-soft'
                      }`}
                    >
                      {p.type === 'PERSONAL_TRAINING' ? 'Personal training' : 'Membership'}
                    </span>
                  </p>
                  <p className="text-xs text-muted tabular">
                    Paid {formatDate(p.paidOn)} ·{' '}
                    {p.type === 'PERSONAL_TRAINING'
                      ? `Personal training${p.coachName ? ` with ${p.coachName}` : ''}`
                      : `covers ${formatDate(p.periodStart)} → ${formatDate(p.periodEnd)}`}
                  </p>
                  {p.voided && p.voidReason && (
                    <p className="text-xs text-bad-ink">Voided: {p.voidReason}</p>
                  )}
                </div>
                {p.voided ? (
                  <span className="shrink-0 rounded-pill bg-bad-soft px-2 py-0.5 text-xs font-medium text-bad-ink">Voided</span>
                ) : isOwner ? (
                  <button
                    type="button"
                    onClick={() => setVoidTarget(p)}
                    disabled={voidingId === p.id}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium text-ink-soft transition-colors hover:bg-bad-soft hover:text-bad-ink disabled:opacity-50"
                  >
                    <Ban className="size-3.5" aria-hidden />
                    {voidingId === p.id ? 'Voiding…' : 'Void'}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-muted">No payments recorded yet.</p>
        )}
      </Card>

      {voidTarget && (
        <VoidPaymentModal
          description={`${money(voidTarget.amount)} · ${voidTarget.method}. It stays in the history (struck-through) but no longer counts, and the membership/PT is recalculated.`}
          onClose={() => setVoidTarget(null)}
          onConfirm={doVoidPayment}
        />
      )}
    </section>
  );
}

function DeleteMemberModal({
  member,
  hasRealPayments,
  onClose,
  onDeactivate,
  onDeleted,
}: {
  member: MemberDetail;
  hasRealPayments: boolean;
  onClose: () => void;
  onDeactivate: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = name.trim().toLowerCase() === member.fullName.trim().toLowerCase();

  async function confirmDelete() {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMember(member.id, name.trim());
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the member. Please try again.');
      setBusy(false);
    }
  }

  // Members with real payments can't be deleted — steer to deactivate.
  if (hasRealPayments) {
    return (
      <Modal open onClose={onClose} title="Delete member" description="This member has payment history.">
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-control bg-bad-soft px-3.5 py-3 text-sm text-bad-ink">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              <strong>{member.fullName}</strong> has payments on record, so they can’t be deleted — that would erase
              real revenue. <strong>Deactivate</strong> them instead: they leave the active list and stop counting
              toward your plan’s member limit, but their payment history is kept.
            </span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={onDeactivate}>
              <UserX className="size-4" aria-hidden /> Deactivate instead
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Delete member" description="This permanently deletes the member and all of their data. There is no undo.">
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-control bg-bad-soft px-3.5 py-3 text-sm text-bad-ink">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Goals, workouts, attendance, notifications and guest passes for <strong>{member.fullName}</strong> will be
            erased. Their login is kept but loses access.
          </span>
        </div>
        <Input
          label={`Type “${member.fullName}” to confirm`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={member.fullName}
          autoFocus
        />
        {error && (
          <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">{error}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={!matches || busy} loading={busy} className="bg-bad-ink text-on-dark hover:bg-bad-ink/90">
            <Trash2 className="size-4" aria-hidden /> Delete permanently
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
      <ArrowLeft className="size-4" aria-hidden /> Members
    </button>
  );
}

function ContactRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-muted">{label}</p>
        <p className="font-medium text-ink tabular">{value}</p>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted">{label}</span>
      <span className="font-medium text-ink tabular">{value}</span>
    </div>
  );
}
